import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { WorkspaceLocation } from 'src/models/workspaceLocation.interface';
import type { AppThunk } from 'src/store';
import { resetWorkspace } from 'src/store/extraActions';
import axiosCore from 'src/utils/axiosCore';

interface WorkspaceLocationState {
  locations: WorkspaceLocation[];
  selectedLocationId: string | null;
  reload: boolean;
}

const initialState: WorkspaceLocationState = {
  locations: [],
  selectedLocationId: null,
  reload: false
};

const slice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    getWorkspaceLocations(
      state: WorkspaceLocationState,
      action: PayloadAction<WorkspaceLocation[]>
    ) {
      state.locations = action.payload;
    },
    createWorkspaceLocation(
      state: WorkspaceLocationState,
      action: PayloadAction<WorkspaceLocation>
    ) {
      const location = action.payload;

      state.locations = [...state.locations, location];
    },
    selectWorkspaceLocation(
      state: WorkspaceLocationState,
      action: PayloadAction<{ location: WorkspaceLocation }>
    ) {
      const { location } = action.payload;

      state.locations = [
        ...state.locations.filter((r) => r._id !== location._id),
        location
      ];
      state.selectedLocationId = location._id;
    },
    updateWorkspaceLocation(
      state: WorkspaceLocationState,
      action: PayloadAction<WorkspaceLocation>
    ) {
      const location = action.payload;

      state.locations = state.locations.map((_location) => {
        if (_location._id === location._id) {
          return location;
        }

        return _location;
      });
    },
    deleteWorkspaceLocation(
      state: WorkspaceLocationState,
      action: PayloadAction<{ locationId: string }>
    ) {
      const { locationId } = action.payload;

      state.locations = state.locations.filter(
        (_location) => _location._id !== locationId
      );
      state.reload = true;
    }
  },
  extraReducers: {
    [resetWorkspace.type]() {
      return initialState;
    }
  }
});

export const reducer = slice.reducer;

export const getWorkspaceLocations =
  (workspaceId: string): AppThunk =>
  async (dispatch) => {
    const response = await axiosCore.get<{ data: any }>(
      `/workspaces/${workspaceId}/location?includeZones=true`
    );
    dispatch(slice.actions.getWorkspaceLocations(response.data.data));
  };

export const createWorkspaceLocation =
  (workspaceId: string, data: any): AppThunk =>
  async (dispatch) => {
    const response = await axiosCore.post<{ data: any }>(
      `/workspaces/${workspaceId}/location`,
      data
    );

    dispatch(slice.actions.createWorkspaceLocation(response.data.data));
  };

export const selectWorkspaceLocation =
  (workspaceId: string, locationId?: string): AppThunk =>
  async (dispatch) => {
    const response = await axiosCore.get<{ data: any }>(
      `/workspaces/${workspaceId}/location/${locationId}`
    );

    dispatch(slice.actions.selectWorkspaceLocation(response.data.data));
  };

export const updateWorkspaceLocation =
  (workspaceId: string, locationId: string, update: any): AppThunk =>
  async (dispatch) => {
    const response = await axiosCore.put<{ data: any }>(
      `/workspaces/${workspaceId}/location/${locationId}`,
      {
        ...update
      }
    );

    dispatch(slice.actions.updateWorkspaceLocation(response.data.data));
  };

export const deleteWorkspaceLocation =
  (workspaceId: string, locationId: string): AppThunk =>
  async (dispatch) => {
    await axiosCore.delete(`/workspaces/${workspaceId}/location/${locationId}`);
    dispatch(slice.actions.deleteWorkspaceLocation({ locationId }));
  };

export default slice;
